import React, { useState, useRef, useEffect } from "react";
import emailjs from "@emailjs/browser";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";
import { toast } from "react-toastify";

const socialLinks = [
  { name: "GitHub", href: "https://github.com/Yashya14", icon: "Github" },
  {
    name: "LinkedIn",
    href: "https://www.linkedin.com/in/yash-gajanan-pal",
    icon: "Linkedin",
  },
  {
    name: "Hackerrank",
    href: "https://www.hackerrank.com/profile/Yashya14",
    icon: "Heading",
  },
];

const ContactUs = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitCount, setSubmitCount] = useState(0);
  const form = useRef();

  // Reset submission status after showing success message
  useEffect(() => {
    let timer;
    if (submitted) {
      timer = setTimeout(() => {
        setSubmitted(false);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [submitted]);

  // validation functions
  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value.trim()) return "Name is required";
        if (value.trim().length < 3)
          return "Name must be at least 3 characters";
        if (value.trim().length > 50) return "Name cannot exceed 50 characters";
        if (!/^[a-zA-Z\s.'-]+$/.test(value.trim()))
          return "Name contains invalid characters";
        return null;

      case "email":
        if (!value.trim()) return "Email is required";
        // More comprehensive email regex
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(value.trim()))
          return "Please enter a valid email address";
        if (value.length > 100) return "Email address is too long";
        return null;

      case "subject":
        if (!value.trim()) return "Subject is required";
        if (value.trim().length < 3)
          return "Subject must be at least 3 characters";
        if (value.trim().length > 100)
          return "Subject cannot exceed 100 characters";
        return null;

      case "message":
        if (!value.trim()) return "Message is required";
        if (value.trim().length < 10)
          return "Message must be at least 10 characters";
        if (value.trim().length > 3000)
          return "Message cannot exceed 3000 characters";
        return null;

      default:
        return null;
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Validate each field
    Object.keys(formData).forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Inline validation as user types
    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    // Validate on blur for better user experience
    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) return;

    // Basic rate limiting (3 submissions per minute)
    if (submitCount >= 3) {
      toast.error("Too many attempts. Please try again later.");
      return;
    }

    setLoading(true);
    setSubmitCount((prev) => prev + 1);

    // Sanitize inputs before sending
    const sanitizedData = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      subject: formData.subject.trim(),
      message: formData.message.trim(),
    };

    // Update form data with sanitized values
    for (const field in sanitizedData) {
      form.current.elements[field].value = sanitizedData[field];
    }

    try {
      await emailjs.sendForm(
        import.meta.env.VITE_EMAILJS_SERVICE_ID,
        import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
        form.current,
        import.meta.env.VITE_EMAILJS_PUBLIC_KEY
      );

      // Success handlinga
      setFormData({ name: "", email: "", subject: "", message: "" });
      form.current.reset();
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      // Error handling with more descriptive messages
      const errorMsg = error.text || "Unknown error";
      console.error("EmailJS error:", error);
      toast.error(`Failed to send message: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    form.current.reset();
    setFormData({ name: "", email: "", subject: "", message: "" });
    setErrors({});
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <section className="pt-24 pb-12 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <div>
                <h1 className="text-4xl lg:text-5xl font-bold text-foreground">
                  Contact <span className="text-conversion-accent">Me</span>
                </h1>
                <p className="text-xl text-muted-foreground mt-2">
                  Let's build something great together
                </p>
              </div>
            </div>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Have a question, project idea, or opportunity? Drop a message and
              I'll get back within 24–48 hours.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Social Links */}
            <div className="space-y-4">
              <div className="p-5 bg-card border border-border rounded-xl">
                <div className="text-sm text-muted-foreground mb-3">Social</div>
                <div className="flex items-center gap-3">
                  {socialLinks.map((net) => (
                    <a
                      key={net.name}
                      href={net.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Connect with me on ${net.name}`}
                      className="p-2 rounded-lg border border-border bg-background/40 hover:border-conversion-accent/40 hover:text-conversion-accent transition-smooth"
                    >
                      <Icon name={net.icon} size={18} />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              {submitted ? (
                <div className="bg-card border border-border rounded-xl p-6 text-center">
                  <div className="mb-4 text-conversion-accent">
                    <Icon name="CheckCircle" size={48} className="mx-auto" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Thank You!</h3>
                  <p className="text-muted-foreground mb-4">
                    Your message has been sent successfully. I'll get back to
                    you soon.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="px-4 py-2 bg-conversion-accent text-white rounded-lg hover:bg-conversion-accent/90 transition-smooth"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form
                  ref={form}
                  onSubmit={onSubmit}
                  className="bg-card border border-border rounded-xl p-6"
                  aria-label="Contact form"
                  noValidate
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm text-muted-foreground mb-1"
                      >
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        aria-required="true"
                        value={formData.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        aria-invalid={!!errors.name}
                        aria-describedby={
                          errors.name ? "name-error" : undefined
                        }
                        className={`w-full px-3 py-2 bg-card border ${
                          errors.name ? "border-red-500" : "border-border"
                        } rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-conversion-accent/50`}
                        placeholder="Your name"
                      />
                      {errors.name && (
                        <p
                          id="name-error"
                          className="mt-1 text-sm text-red-500"
                        >
                          {errors.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm text-muted-foreground mb-1"
                      >
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        aria-required="true"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        aria-invalid={!!errors.email}
                        aria-describedby={
                          errors.email ? "email-error" : undefined
                        }
                        className={`w-full px-3 py-2 bg-card border ${
                          errors.email ? "border-red-500" : "border-border"
                        } rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-conversion-accent/50`}
                        placeholder="you@example.com"
                      />
                      {errors.email && (
                        <p
                          id="email-error"
                          className="mt-1 text-sm text-red-500"
                        >
                          {errors.email}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-4">
                    <label
                      htmlFor="subject"
                      className="block text-sm text-muted-foreground mb-1"
                    >
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="subject"
                      name="subject"
                      type="text"
                      required
                      aria-required="true"
                      value={formData.subject}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      aria-invalid={!!errors.subject}
                      aria-describedby={
                        errors.subject ? "subject-error" : undefined
                      }
                      className={`w-full px-3 py-2 bg-card border ${
                        errors.subject ? "border-red-500" : "border-border"
                      } rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-conversion-accent/50`}
                      placeholder="How can I help?"
                    />
                    {errors.subject && (
                      <p
                        id="subject-error"
                        className="mt-1 text-sm text-red-500"
                      >
                        {errors.subject}
                      </p>
                    )}
                  </div>

                  <div className="mt-4">
                    <label
                      htmlFor="message"
                      className="block text-sm text-muted-foreground mb-1"
                    >
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      required
                      aria-required="true"
                      value={formData.message}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      aria-invalid={!!errors.message}
                      aria-describedby={
                        errors.message ? "message-error" : undefined
                      }
                      className={`w-full px-3 py-2 bg-card border ${
                        errors.message ? "border-red-500" : "border-border"
                      } rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-conversion-accent/50`}
                      placeholder="Share details about your project, timeline, and goals..."
                    />
                    {errors.message && (
                      <p
                        id="message-error"
                        className="mt-1 text-sm text-red-500"
                      >
                        {errors.message}
                      </p>
                    )}
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-conversion-accent text-white font-semibold hover:bg-conversion-accent/90 transition-smooth disabled:opacity-60"
                      aria-busy={loading}
                    >
                      <Icon
                        name={loading ? "Loader2" : "Send"}
                        className={loading ? "animate-spin" : ""}
                        size={18}
                        aria-hidden="true"
                      />
                      {loading ? "Sending..." : "Send Message"}
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-card border border-border hover:border-conversion-accent/50 text-foreground transition-smooth"
                    >
                      <Icon name="RotateCcw" size={18} aria-hidden="true" />
                      Clear
                    </button>
                  </div>
                  <div className="mt-4 text-xs text-muted-foreground">
                    <span className="text-red-500">*</span> Required fields
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <footer className="py-8 px-6 lg:px-8 border-t border-border">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Yash Pal. Crafted with ❤️ and passion.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ContactUs;
